import { useState } from 'react';
import { CONTACT_TYPES, type Contact, type ContactInput, type ContactType } from '@stromo/shared';
import {
  useContacts,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
} from '../lib/contacts';
import { apiErrorMessage } from '../lib/api';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CrmImportModal } from '../components/CrmImportModal';
import { useAuthStore } from '../lib/authStore';
import { useCrmStatus } from '../lib/crm';

const TYPE_LABELS: Record<ContactType, string> = {
  architect: 'Architekt',
  client: 'Bauherr',
  gu: 'Generalunternehmer',
  other: 'Sonstig',
};

const TYPE_COLORS: Record<ContactType, { bg: string; fg: string }> = {
  architect: { bg: 'var(--color-info-bg)', fg: 'var(--color-info-text)' },
  client: { bg: 'var(--color-planning-bg)', fg: 'var(--color-planning-text)' },
  gu: { bg: 'var(--color-success-bg)', fg: 'var(--color-success-text)' },
  other: { bg: 'var(--color-open-bg)', fg: 'var(--color-open-text)' },
};

const emptyForm = (): ContactInput => ({
  name: '',
  contact_types: ['client'],
  email: null,
  phone: null,
  notes: null,
});

function ContactTypeChip({ type }: { type: ContactType }) {
  const { bg, fg } = TYPE_COLORS[type];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 600,
        marginRight: 4,
      }}
    >
      {TYPE_LABELS[type]}
    </span>
  );
}

export function ContactsPage() {
  const { user } = useAuthStore();
  const canDelete = user?.role === 'admin';
  const canImport = user?.role === 'admin' || user?.role === 'project_manager';
  const contacts = useContacts();
  const createMut = useCreateContact();
  const updateMut = useUpdateContact();
  const deleteMut = useDeleteContact();
  const crmStatus = useCrmStatus();

  const [showForm, setShowForm] = useState(false);
  const [showCrmImport, setShowCrmImport] = useState(false);
  const [editTarget, setEditTarget] = useState<Contact | null>(null);
  const [form, setForm] = useState<ContactInput>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [filter, setFilter] = useState<ContactType | ''>('');

  const open = (target?: Contact) => {
    if (target) {
      setEditTarget(target);
      setForm({
        name: target.name,
        contact_types: target.contact_types,
        email: target.email,
        phone: target.phone,
        notes: target.notes,
      });
    } else {
      setEditTarget(null);
      setForm(emptyForm());
    }
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditTarget(null);
    createMut.reset();
    updateMut.reset();
  };

  const save = () => {
    if (form.contact_types.length === 0) return;
    if (editTarget) {
      updateMut.mutate(
        { id: editTarget.id, patch: form },
        { onSuccess: close },
      );
    } else {
      createMut.mutate(form, { onSuccess: close });
    }
  };

  const toggleType = (type: ContactType) => {
    setForm((f) => {
      const has = f.contact_types.includes(type);
      const next = has
        ? f.contact_types.filter((t) => t !== type)
        : [...f.contact_types, type];
      return { ...f, contact_types: next };
    });
  };

  const filtered = (contacts.data ?? []).filter(
    (c) => !filter || c.contact_types.includes(filter),
  );
  const saveError = createMut.isError
    ? apiErrorMessage(createMut.error)
    : updateMut.isError
    ? apiErrorMessage(updateMut.error)
    : null;

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Kontakte</div>
        <div className="topbar-actions">
          {canImport && crmStatus.data?.enabled && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowCrmImport(true)}
              disabled={!crmStatus.data.ready}
              title={
                crmStatus.data.ready
                  ? 'Kontakt aus dem CRM (Braso) importieren'
                  : `CRM nicht erreichbar${crmStatus.data.error ? `: ${crmStatus.data.error}` : ''}`
              }
            >
              ↓ Aus CRM
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => open()}>
            + Neuer Kontakt
          </button>
        </div>
      </header>

      <div className="content">
        <div className="filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value as ContactType | '')}>
            <option value="">Alle Typen</option>
            {CONTACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          {contacts.isLoading ? (
            <div className="loading">Lade …</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state"><p>Keine Kontakte vorhanden.</p></div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typen</th>
                  <th>E-Mail</th>
                  <th>Telefon</th>
                  <th>Bemerkungen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Name" style={{ fontWeight: 600, color: 'var(--color-navy)' }}>{c.name}</td>
                    <td data-label="Typen">
                      {c.contact_types.map((t) => (
                        <ContactTypeChip key={t} type={t} />
                      ))}
                    </td>
                    <td data-label="E-Mail">{c.email ?? '—'}</td>
                    <td data-label="Telefon">{c.phone ?? '—'}</td>
                    <td data-label="Bemerkungen" style={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.notes ?? '—'}
                    </td>
                    <td data-label="" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(c)}>
                        Bearbeiten
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteTarget(c)}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        open={showForm}
        title={editTarget ? `Kontakt: ${editTarget.name}` : 'Neuer Kontakt'}
        onClose={close}
        width={560}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={close}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={
                !form.name.trim() ||
                form.contact_types.length === 0 ||
                createMut.isPending ||
                updateMut.isPending
              }
            >
              {createMut.isPending || updateMut.isPending ? 'Speichere …' : editTarget ? 'Speichern' : 'Anlegen'}
            </button>
          </>
        }
      >
        {saveError && <div className="login-error">{saveError}</div>}
        <div className="form-grid">
          <div className="form-group full">
            <label className="form-label">Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group full">
            <label className="form-label">Typen * (Mehrfach-Auswahl möglich)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CONTACT_TYPES.map((t) => {
                const active = form.contact_types.includes(t);
                const { bg, fg } = TYPE_COLORS[t];
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggleType(t)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: `2px solid ${active ? fg : 'var(--color-border-strong)'}`,
                      background: active ? bg : 'var(--color-surface)',
                      color: active ? fg : 'var(--color-text-muted)',
                      fontSize: 13.5,
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                      transition: 'all .12s',
                    }}
                  >
                    {active ? '✓ ' : ''}
                    {TYPE_LABELS[t]}
                  </button>
                );
              })}
            </div>
            {form.contact_types.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>
                Mindestens ein Typ wählen.
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">E-Mail</label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value || null })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Telefon</label>
            <input
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value || null })}
            />
          </div>
          <div className="form-group full">
            <label className="form-label">Bemerkungen</label>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
            />
          </div>
        </div>
      </Modal>

      <CrmImportModal
        open={showCrmImport}
        onClose={() => setShowCrmImport(false)}
        onPick={(input) => {
          setShowCrmImport(false);
          // Pre-fill mit Braso-Daten und Form öffnen — User wählt contact_types
          // und speichert. external_id+source bleiben im Form-State und werden
          // beim Anlegen mit übergeben.
          setEditTarget(null);
          setForm(input);
          setShowForm(true);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Kontakt löschen"
        danger
        message={deleteTarget ? `Kontakt "${deleteTarget.name}" endgültig löschen?` : ''}
        confirmLabel="Löschen"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMut.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }
        }}
      />
    </>
  );
}
